<template>
  <div
    class="manual-selection-wrapper"
    :class="{ selected: props.checked }"
    ref="containerRef"
  >
    <CFlex align="center" class="manual-radio-wrapper" @click="handleManualSelection">
      <CRadioIcon :active="props.checked" />
      <p class="manual-selection-title">{{ $t("driveBooking.manualEntry") }}</p>
    </CFlex>
    <transition @enter="onEnter" @after-enter="onAfterEnter" @leave="onLeave">
      <div v-if="props.checked" class="manual-selection-content" ref="contentRef">
        <div class="manual-selection-inner" ref="innerRef">
          <CFlex align="stretch" class="manual-selection-row">
            <div class="manual-selection-field-wrapper">
              <CField
                type="text"
                :label="$t('driveBooking.licensePlateNumber')"
                v-model="licensePlateModel"
                @update:modelValue="
                  (e) => {
                    $emit('changeLicensePlate', e);
                  }
                "
                :validators="licensePlateInputValidator"
              />
            </div>
            <div class="manual-selection-field-wrapper">
              <CField
                type="text"
                :label="$t('driveBooking.brandAndModel')"
                v-model="carModelModel"
                @update:modelValue="
                  (e) => {
                    $emit('changeModel', e);
                  }
                "
                :validators="descriptionInputValidator"
              />
            </div>
          </CFlex>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue';
  import CField from '@/shared/components/form/CField.vue';
  import CRadioIcon from '@/shared/components/form/CRadioIcon.vue';
  import DriveReservationRequestType from '@/driveBookings/constants/DriveReservationRequestType';
  import { maxLength, required } from '@/shared/utils/form/validation';
  import { useExpandableContent } from '@/shared/utils/useExpandableContent';
  import CFlex from '@/shared/components/CFlex.vue';

  const props = defineProps<{
    checked: boolean;
    licensePlate?: string;
    carModel?: string;
  }>();

  const { contentRef, innerRef, onEnter, onAfterEnter, onLeave } = useExpandableContent();
  const containerRef = ref<HTMLElement | null>(null);

  const licensePlateInputValidator = {
    required,
    licencePlate: maxLength(15),
  };

  const descriptionInputValidator = {
    required,
    maxLength: maxLength(25),
  };

  const licensePlateModel = computed({
    get: () => props.licensePlate || '',
    set: (value) => emit('changeLicensePlate', value),
  });

  const carModelModel = computed({
    get: () => props.carModel || '',
    set: (value) => emit('changeModel', value),
  });

  const emit = defineEmits<{
    (e: 'change', v: string): void;
    (e: 'changeLicensePlate', v: string): void;
    (e: 'changeModel', v: string): void;
  }>();

  const handleManualSelection = () => {
    emit('change', DriveReservationRequestType.MANUAL);
  };
</script>

<style lang="scss" scoped>
.manual-selection-wrapper {
  border: 1px solid $neutral-300;
  border-radius: 5px;
  background-color: $neutral-0;
}

.manual-radio-wrapper {
  cursor: pointer;
  padding-left: 16px;
  height: 56px;
}

.manual-selection-title {
  font-weight: 500;
  line-height: 7px;
  color: #212528;
}

.manual-selection-content {
  transition: height 400ms;
  transition-timing-function: cubic-bezier(0.22, 0.98, 0.3, 1);
  padding: 0px 12px 0px 16px;
}

.manual-selection-inner {
  padding-block: 0 7px;
}

.manual-selection-row {
  margin-bottom: 8px;
}

:deep(.manual-selection-row.c-flex-gap) {
  gap: 12px;
}

.manual-selection-field-wrapper {
  flex: 1;
}

.selected {
  box-shadow: 0px 0px 2px 0px #0000004d, 0px 4px 8px 0px #0000001a;
}
</style>
